import StaffCaregiverSettings from '../features/staffNotes/StaffCaregiverSettings';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  TextField
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRole } from '../contexts/RoleContext';
import { useChildContext } from '../contexts/ChildContext';
import { canManageTeam, getHighestPriorityRole, USER_ROLES } from '../constants/roles';
import ResponsiveLayout from '../components/Layout/ResponsiveLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { populateChildTeamMembers } from '../services/rolePermissionService';
import { listChildInvitations, resendInvitation, editInvitation, cancelInvitation } from '../services/invitationService';
import { auth } from '../services/firebase';
import CareTeamPageHeader from '../components/CareTeam/CareTeamPageHeader';
import CareTeamChildSection from '../components/CareTeam/CareTeamChildSection';
import { collectTeamMembers, getReadableRoleLabel } from '../components/CareTeam/careTeamPresentation';
import { getCareTeamLensCopy } from '../components/CareTeam/careTeamLensCopy';
import ChildShellBottomNav from '../components/Dashboard/shared/ChildShellBottomNav';
import { getChildAccentTheme } from '../components/Dashboard/shared/childAccentTheme';
import { ACTIVE_CHILD_STORAGE_KEY } from '../components/Dashboard/shared/DashboardViewContext';
import colors from '../assets/theme/colors';

const INVITE_ROLE_OPTIONS = [
  USER_ROLES.CARE_PARTNER,
  USER_ROLES.CAREGIVER,
  USER_ROLES.THERAPIST,
];

const CareTeamPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const focusChildId = location.state?.focusChildId || location.state?.childId || currentChildId || null;
  const { childrenWithAccess, getUserRoleForChild, loading } = useRole();
  const [shellChildId, setShellChildId] = useState(focusChildId || currentChildId || '');
  const [hasAccess, setHasAccess] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [enrichedChildren, setEnrichedChildren] = useState([]);
  const [childInvitations, setChildInvitations] = useState({});
  const [inviteLoading, setInviteLoading] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState(null);
  const [editingInvite, setEditingInvite] = useState(null);
  const [editingInviteEmail, setEditingInviteEmail] = useState('');
  const [editingInviteMessage, setEditingInviteMessage] = useState('');
  const [editingInviteRole, setEditingInviteRole] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [cancelingInviteId, setCancelingInviteId] = useState(null);
  const selectedChild = useMemo(() => {
    if (!childrenWithAccess.length) return null;
    return childrenWithAccess.find((child) => child.id === shellChildId)
      || childrenWithAccess.find((child) => child.id === focusChildId)
      || childrenWithAccess.find((child) => child.id === currentChildId)
      || childrenWithAccess[0];
  }, [childrenWithAccess, currentChildId, focusChildId, shellChildId]);
  const selectedChildId = selectedChild?.id || '';
  const childAccent = useMemo(
    () => getChildAccentTheme(selectedChild || {}, { children: childrenWithAccess }),
    [selectedChild, childrenWithAccess]
  );
  const careTeamCopy = useMemo(
    () => getCareTeamLensCopy(selectedChild || {}),
    [selectedChild]
  );

  const getInviteStatusTone = (invite) => {
    const status = String(invite?.status || '').toLowerCase();
    if (['failed', 'bounced', 'complained'].includes(status)) return 'error';
    if (['pending'].includes(status)) return 'warning';
    if (['sent', 'resent'].includes(status)) return 'info';
    if (['accepted'].includes(status)) return 'success';
    return 'default';
  };

  const getInviteStatusLabel = (invite) => {
    const status = String(invite?.status || 'pending').toLowerCase();
    if (status === 'resent') return 'resent';
    if (status === 'bounced') return 'bounced';
    if (status === 'complained') return 'complained';
    if (status === 'accepted') return 'accepted';
    if (status === 'canceled' || status === 'cancelled') return 'canceled';
    if (status === 'failed') return 'failed';
    if (status === 'sent') return 'sent';
    return 'pending';
  };

  // Simple access check and data loading
  useEffect(() => {
    const handleActiveChildChanged = (event) => {
      const nextChildId = event?.detail?.childId
        || window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY)
        || '';
      if (nextChildId) {
        setShellChildId(nextChildId);
        setCurrentChildId?.(nextChildId);
      }
    };

    window.addEventListener('captureez:active-child-changed', handleActiveChildChanged);
    return () => window.removeEventListener('captureez:active-child-changed', handleActiveChildChanged);
  }, [setCurrentChildId]);

  useEffect(() => {
    const loadTeamData = async () => {
      if (!loading && selectedChild) {
        const visibleChildren = [selectedChild];
        const availableRoles = visibleChildren
          .map((child) => getUserRoleForChild?.(child.id) || null)
          .filter(Boolean);

        setHasAccess(availableRoles.length > 0);
        setUserRole(getHighestPriorityRole(availableRoles) || null);
        setCurrentChildId?.(selectedChild.id);

        // Load team member details for the selected child.
        if (availableRoles.length > 0) {
          try {
            const enrichedChildrenData = [];
            for (const child of visibleChildren) {
              const enrichedChild = await populateChildTeamMembers(child);
              enrichedChildrenData.push(enrichedChild);
            }
            setEnrichedChildren(enrichedChildrenData);

            if (enrichedChildrenData.some((child) => canManageTeam(getUserRoleForChild?.(child.id) || child.userRole))) {
              setInviteLoading(true);
              const invitePairs = await Promise.all(
                enrichedChildrenData.map(async (child) => {
                  try {
                    const invites = await listChildInvitations(child.id);
                    return [child.id, invites];
                  } catch (error) {
                    console.warn(`Error loading invitations for ${child.id}:`, error);
                    return [child.id, []];
                  }
                })
              );

              setChildInvitations(Object.fromEntries(invitePairs));
              setInviteLoading(false);
            }
          } catch (error) {
            console.error('Error loading team data:', error);
            setInviteLoading(false);
          }
        }
      } else if (!loading) {
        setHasAccess(false);
        setUserRole(null);
        setEnrichedChildren([]);
        setChildInvitations({});
      }
    };

    loadTeamData();
  }, [getUserRoleForChild, loading, selectedChild, setCurrentChildId]);

  const reloadInvitationsForChild = async (childId) => {
    try {
      const invites = await listChildInvitations(childId);
      setChildInvitations((prev) => ({
        ...prev,
        [childId]: invites,
      }));
    } catch (error) {
      console.warn('Failed to reload invitations for child', childId, error);
    }
  };

  const handleResendInvite = async (invite) => {
    try {
      setResendingInviteId(invite.id);
      await resendInvitation(invite.id);
      await reloadInvitationsForChild(invite.childId);
    } catch (error) {
      console.error('Error resending invitation:', error);
    } finally {
      setResendingInviteId(null);
    }
  };

  const openEditInvite = (invite) => {
    setEditingInvite(invite);
    setEditingInviteEmail(invite.recipientEmail || '');
    setEditingInviteMessage(invite.personalMessage || '');
    setEditingInviteRole(invite.role || '');
  };

  const closeEditInvite = () => {
    setEditingInvite(null);
    setEditingInviteEmail('');
    setEditingInviteMessage('');
    setEditingInviteRole('');
  };

  const handleSaveInviteEdit = async () => {
    if (!editingInvite) return;

    try {
      setEditSaving(true);
      await editInvitation(
        editingInvite.id,
        editingInviteEmail,
        editingInviteRole,
        editingInviteMessage
      );
      await reloadInvitationsForChild(editingInvite.childId);
      closeEditInvite();
    } catch (error) {
      console.error('Error updating invitation:', error);
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancelInvite = async (invite) => {
    try {
      setCancelingInviteId(invite.id);
      await cancelInvitation(invite.id);
      await reloadInvitationsForChild(invite.childId);
    } catch (error) {
      console.error('Error canceling invitation:', error);
    } finally {
      setCancelingInviteId(null);
    }
  };

  const inviteSummary = useMemo(() => {
    const summary = {
      total: 0,
      pending: 0,
      sent: 0,
      resent: 0,
      failed: 0,
      bounced: 0,
      complained: 0,
      accepted: 0,
    };

    Object.values(childInvitations).flat().forEach((invite) => {
      summary.total += 1;
      const status = String(invite?.status || 'pending').toLowerCase();
      if (summary[status] !== undefined) {
        summary[status] += 1;
      }
    });

    return summary;
  }, [childInvitations]);

  const totalMemberCount = useMemo(
    () => enrichedChildren.reduce((sum, child) => sum + collectTeamMembers(child).length, 0),
    [enrichedChildren]
  );

  const currentUserId = auth.currentUser?.uid || null;

  const compactSummaryText = useMemo(() => {
    const totalPending = inviteSummary.pending + inviteSummary.sent + inviteSummary.resent;
    const hasAnyInvites = inviteSummary.total > 0;
    const hasAnyMembers = totalMemberCount > 0;
    if (!hasAnyInvites && !hasAnyMembers) {
      return '';
    }

    return `${totalMemberCount} ${totalMemberCount === 1 ? 'member' : 'members'} · ${totalPending} pending ${totalPending === 1 ? 'invite' : 'invites'}`;
  }, [inviteSummary, totalMemberCount]);

  const navigateToDashboard = useCallback((target = 'today') => {
    if (selectedChildId) {
      setCurrentChildId?.(selectedChildId);
    }

    navigate('/dashboard', {
      state: {
        selectedChildId: selectedChildId || null,
        childShellTarget: target,
      },
    });
  }, [navigate, selectedChildId, setCurrentChildId]);

  const handleChildShellNavSelect = useCallback((itemId) => {
    if (itemId === 'home') {
      navigate('/home', { state: { selectedChildId: selectedChildId || null } });
      return;
    }

    if (itemId === 'profile') {
      navigate('/care-profile', {
        state: {
          childId: selectedChildId || null,
        },
      });
      return;
    }

    if (itemId === 'today' || itemId === 'tools') {
      navigateToDashboard(itemId);
    }
  }, [navigate, navigateToDashboard, selectedChildId]);

  if (loading) {
    return (
      <ResponsiveLayout pageTitle="Care Circle" showHeaderBar={false}>
        <LoadingSpinner message="Loading..." />
      </ResponsiveLayout>
    );
  }

  if (!hasAccess) {
    return (
      <ResponsiveLayout pageTitle="Care Circle" showHeaderBar={false}>
        <Alert severity="warning">
          You don't have permission to access the care circle management page.
        </Alert>
      </ResponsiveLayout>
    );
  }

  const handleInviteTeamMember = (childId = null) => {
    navigate('/invite', childId ? { state: { childId } } : undefined);
  };

  const handleOpenChildChat = (childId) => {
    navigate(`/messages?childId=${childId}&openChildChat=1&returnToDashboard=1`, {
      state: {
        selectedChildId: childId,
        openChildChat: true,
        returnToDashboard: true,
      },
    });
  };

  return (
    <ResponsiveLayout pageTitle="Care Circle" showSidebar={false} showFab={false} showHeaderBar={false} showBottomNav={false}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: colors.semantic.sageMist,
          pb: { xs: 'calc(6rem + env(safe-area-inset-bottom, 0px))', md: 3 },
        }}
      >
        <CareTeamPageHeader
          onBack={() => navigateToDashboard('today')}
          userRole={userRole}
          summaryText={compactSummaryText}
          child={selectedChild}
          showBack={false}
        />

        {enrichedChildren.length === 0 ? (
          <Alert severity="info">
            Loading team members...
          </Alert>
        ) : (
          <Box>
            {enrichedChildren.map((child) => (
              <React.Fragment key={child.id}>
              <CareTeamChildSection
                child={child}
                viewerRole={getUserRoleForChild?.(child.id) || child.userRole || userRole}
                currentUserId={currentUserId}
                invites={childInvitations[child.id] || []}
                inviteLoading={inviteLoading}
                onInviteChild={handleInviteTeamMember}
                onOpenChat={handleOpenChildChat}
                getInviteStatusLabel={getInviteStatusLabel}
                getInviteStatusTone={getInviteStatusTone}
                onResendInvite={handleResendInvite}
                onEditInvite={openEditInvite}
                onCancelInvite={handleCancelInvite}
                resendingInviteId={resendingInviteId}
                cancelingInviteId={cancelingInviteId}
              />
              <StaffCaregiverSettings child={child} canManage={canManageTeam(getUserRoleForChild?.(child.id) || child.userRole || userRole)} />
              </React.Fragment>
            ))}
          </Box>
        )}

        <ChildShellBottomNav
          activeItem=""
          accent={childAccent}
          onSelect={handleChildShellNavSelect}
        />
      </Box>

      <Dialog open={Boolean(editingInvite)} onClose={closeEditInvite} fullWidth maxWidth="sm">
        <DialogTitle>Edit invitation</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 0.5, mb: 1.5, color: 'text.secondary', fontSize: '0.95rem', lineHeight: 1.45 }}>
            Update the email or role, then send a fresh invitation. The previous email cannot be recalled.
          </Typography>
          <TextField
            fullWidth
            label="Recipient Email"
            type="email"
            value={editingInviteEmail}
            onChange={(e) => setEditingInviteEmail(e.target.value)}
            sx={{ mt: 1.5 }}
          />
          <TextField
            select
            fullWidth
            label="Role"
            value={editingInviteRole}
            onChange={(e) => setEditingInviteRole(e.target.value)}
            sx={{ mt: 2 }}
            helperText={careTeamCopy.editInviteRoleHelper}
          >
            {INVITE_ROLE_OPTIONS.map((role) => (
              <MenuItem key={role} value={role}>
                {getReadableRoleLabel(role)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Optional note for the new email"
            multiline
            minRows={3}
            value={editingInviteMessage}
            onChange={(e) => setEditingInviteMessage(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Use this only if you want to explain why the invite is being resent."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditInvite} disabled={editSaving}>Close</Button>
          <Button onClick={handleSaveInviteEdit} variant="contained" disabled={editSaving}>
            {editSaving ? 'Sending...' : 'Save and resend invite'}
          </Button>
        </DialogActions>
      </Dialog>

    </ResponsiveLayout>
  );
};

export default CareTeamPage;
